package gr.tech.site.components

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.overflow
import com.varabyte.kobweb.compose.ui.modifiers.width
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.rememberPageContext
import gr.tech.site.data.StateTier
import gr.tech.site.data.allMapStateKeys
import gr.tech.site.data.allStates
import gr.tech.site.data.statePageRoutes
import kotlinx.browser.window
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.dom.Div

/**
 * Interactive US state map (48 contiguous states; AK + HI not in SVG).
 *
 * - States in [statePageRoutes] navigate to their dedicated page when clicked
 *   and receive the "favorite-state" CSS class (deeper green).
 * - All other states call [onNoPageClick] with the lowercase 2-letter map key
 *   so the parent can open a utility modal.
 *
 * Each path also receives the "state" CSS class directly (not just via the
 * parent <g> group) so that .state:hover fires per individual path element.
 */
@Composable
fun UsStateMap(onNoPageClick: (stateAbbr: String) -> Unit = {}) {
    val ctx = rememberPageContext()

    // Build a mapKey → tier lookup so each SVG path gets the right CSS class
    val tierByMapKey: Map<String, StateTier> = allStates
        .filter { it.mapKey != null }
        .associate { it.mapKey!! to it.tier }

    Div(attrs = Modifier
        .width(100.percent)
        .overflow(Overflow.Hidden)
        .toAttrs {
            ref { container ->
                window.fetch("/us-map.svg").then { response ->
                    response.text().then { svgText ->
                        container.innerHTML = svgText

                        val svg = container.querySelector("svg")
                        svg?.setAttribute("width", "100%")
                        svg?.removeAttribute("height")

                        // Stamp per-path classes so CSS hover fires per state, not per group.
                        allMapStateKeys.forEach { abbr ->
                            val path = container.querySelector(".$abbr")
                            val tier = tierByMapKey[abbr] ?: StateTier.STANDARD

                            when (tier) {
                                StateTier.FEATURED -> {
                                    path?.classList?.add("state", "favorite-state")
                                    path?.addEventListener("click", {
                                        ctx.router.navigateTo(statePageRoutes.getValue(abbr))
                                    })
                                }
                                StateTier.ACTIVE -> {
                                    path?.classList?.add("state", "tier-active")
                                    path?.addEventListener("click", { onNoPageClick(abbr) })
                                }
                                StateTier.STANDARD -> {
                                    path?.classList?.add("state")
                                    path?.addEventListener("click", { onNoPageClick(abbr) })
                                }
                            }
                        }
                    }
                }
                onDispose { }
            }
        }
    )
}
